/**
 * Reference utilities over the formula syntax tree.
 *
 * Four jobs, all pure functions on `Ast`:
 *
 * - `refsOf` / `namesOf`: what a formula reads, for dependency wiring and for
 *   the in-grid reference highlight while editing (Requirement 2.9).
 * - `translate`: offset relative components, leave `$`-anchored components
 *   alone. This is the copy/paste rule (Requirement 4.12).
 * - `rewriteForStructuralChange`: keep references pointing at the same cells
 *   across row/column insertion and deletion, and produce `#REF!` when the
 *   target is deleted (Requirement 4.13).
 * - `cycleRefAnchor` / `cycleAnchorText`: the `F4` anchor cycle
 *   (`A1 → $A$1 → A$1 → $A1 → A1`, Requirement 3.1).
 *
 * Out-of-sheet results are represented the way Excel does, by replacing the
 * offending reference with the `#REF!` error literal, so a rewritten tree is
 * always a valid tree.
 */

import { MAX_COLS, MAX_ROWS, type Range, type SheetId } from '../types.js';
import type { Ast, RangeNode, RefNode } from './ast.js';
import { parseFormula } from './parser.js';
import { printExpr } from './printer.js';

/** A reference-shaped leaf: a single cell or a range. */
export type RefUse = RefNode | RangeNode;

/** The four structural edits that move cells (Requirement 4.13). */
export type StructuralKind = 'insertRows' | 'deleteRows' | 'insertCols' | 'deleteCols';

/**
 * A structural edit, as seen by the reference rewriter.
 *
 * `sheet` is the sheet *name* as it appears in formulas, not the `SheetId`:
 * this module works on syntax, so the caller resolves ids to names first.
 * `at` is 0-based and `count` is at least 1.
 */
export interface StructuralEdit {
  kind: StructuralKind;
  sheet: string;
  at: number;
  count: number;
}

const REF_ERROR: Ast = { k: 'err', v: '#REF!' };

/** Every reference and range in the tree, in left-to-right source order. */
export function refsOf(ast: Ast): RefUse[] {
  const uses: RefUse[] = [];
  walk(ast, (node) => {
    if (node.k === 'ref' || node.k === 'range') uses.push(node);
  });
  return uses;
}

/** Every Named_Range mentioned in the tree, in source order, with duplicates. */
export function namesOf(ast: Ast): string[] {
  const names: string[] = [];
  walk(ast, (node) => {
    if (node.k === 'name') names.push(node.name);
  });
  return names;
}

/**
 * Resolves a reference use to a normalized {@link Range} on `sheet`, for
 * highlighting and dependency registration. Anchoring is irrelevant once a
 * reference is resolved, so it is dropped here.
 */
export function refUseToRange(use: RefUse, sheet: SheetId): Range {
  const [from, to] = use.k === 'ref' ? [use, use] : [use.from, use.to];
  return {
    sheet,
    r0: Math.min(from.row, to.row),
    c0: Math.min(from.col, to.col),
    r1: Math.max(from.row, to.row),
    c1: Math.max(from.col, to.col),
  };
}

/** The sheet a reference points at: its own qualifier, else the host sheet. */
export function refSheetName(use: RefUse, hostSheet: string): string {
  const ref = use.k === 'ref' ? use : use.from;
  return ref.sheet ?? hostSheet;
}

/**
 * Offsets every relative row and column by `dRow` / `dCol` and leaves every
 * `$`-anchored component untouched (Requirement 4.12). A reference that would
 * land outside the sheet becomes `#REF!`; a range becomes `#REF!` if either
 * endpoint would.
 *
 * With in-bounds results, `translate(translate(ast, d), -d)` is `ast`, which is
 * the invertibility half of the copy/paste rule.
 */
export function translate(ast: Ast, dRow: number, dCol: number): Ast {
  if (dRow === 0 && dCol === 0) return ast;
  return mapRefs(ast, (use) =>
    use.k === 'ref'
      ? translateRef(use, dRow, dCol)
      : combineEndpoints(translateRef(use.from, dRow, dCol), translateRef(use.to, dRow, dCol)),
  );
}

/**
 * Rewrites references so they keep pointing at the same cells after rows or
 * columns are inserted or deleted (Requirement 4.13).
 *
 * Insertion shifts every reference at or after the insertion point, absolute or
 * not, and a range that straddles the insertion point grows. Deletion pulls
 * later references back, clamps a range that overlaps the deleted band, and
 * yields `#REF!` when the whole target disappears.
 *
 * `hostSheet` is the sheet the formula itself lives on; unqualified references
 * are interpreted against it. It defaults to the edited sheet, which is the
 * common case of rewriting a formula on the sheet being edited.
 */
export function rewriteForStructuralChange(
  ast: Ast,
  edit: StructuralEdit,
  hostSheet: string = edit.sheet,
): Ast {
  if (edit.count <= 0) return ast;
  const axis: Axis = edit.kind === 'insertRows' || edit.kind === 'deleteRows' ? 'row' : 'col';
  const inserting = edit.kind === 'insertRows' || edit.kind === 'insertCols';
  return mapRefs(ast, (use) => {
    if (refSheetName(use, hostSheet) !== edit.sheet) return use;
    return use.k === 'ref'
      ? rewriteRef(use, axis, inserting, edit)
      : rewriteRange(use, axis, inserting, edit);
  });
}

/**
 * The `F4` anchor cycle: `A1 → $A$1 → A$1 → $A1 → A1`.
 * Both endpoints of a range cycle together, following the left endpoint.
 */
export function cycleRefAnchor(ref: RefNode): RefNode {
  const next = nextAnchorState(ref.absCol, ref.absRow);
  return withAnchors(ref, next.absCol, next.absRow);
}

/** {@link cycleRefAnchor} for either reference shape. */
export function cycleAnchor(use: RefUse): RefUse {
  if (use.k === 'ref') return cycleRefAnchor(use);
  const next = nextAnchorState(use.from.absCol, use.from.absRow);
  return {
    k: 'range',
    from: withAnchors(use.from, next.absCol, next.absRow),
    to: withAnchors(use.to, next.absCol, next.absRow),
  };
}

/**
 * Cycles the anchoring of a reference token and re-prints it through the
 * canonical printer. Returns `null` when the text is not a single reference or
 * range, which is how the Shortcut_Handler decides to leave the caret alone.
 */
export function cycleAnchorText(token: string): string | null {
  const parsed = parseFormula(token);
  if (!parsed.ok) return null;
  const ast = parsed.ast;
  if (ast.k !== 'ref' && ast.k !== 'range') return null;
  return printExpr(cycleAnchor(ast));
}

/* ------------------------------------------------------------------ internals */

type Axis = 'row' | 'col';

/** `deleted`: the target row/column is gone. `overflow`: pushed off the sheet. */
type AxisResult = { ok: true; value: number } | { ok: false; why: 'deleted' | 'overflow' };

function axisLimit(axis: Axis): number {
  return axis === 'row' ? MAX_ROWS : MAX_COLS;
}

function axisOf(ref: RefNode, axis: Axis): number {
  return axis === 'row' ? ref.row : ref.col;
}

function withAxis(ref: RefNode, axis: Axis, value: number): RefNode {
  return axis === 'row' ? withPosition(ref, value, ref.col) : withPosition(ref, ref.row, value);
}

/** Rebuilds a reference at a new position, preserving anchors and sheet. */
function withPosition(ref: RefNode, row: number, col: number): RefNode {
  const base: RefNode = { k: 'ref', absCol: ref.absCol, col, absRow: ref.absRow, row };
  return ref.sheet === undefined ? base : { ...base, sheet: ref.sheet };
}

function withAnchors(ref: RefNode, absCol: boolean, absRow: boolean): RefNode {
  const base: RefNode = { k: 'ref', absCol, col: ref.col, absRow, row: ref.row };
  return ref.sheet === undefined ? base : { ...base, sheet: ref.sheet };
}

function nextAnchorState(absCol: boolean, absRow: boolean): { absCol: boolean; absRow: boolean } {
  if (!absCol && !absRow) return { absCol: true, absRow: true };
  if (absCol && absRow) return { absCol: false, absRow: true };
  if (!absCol && absRow) return { absCol: true, absRow: false };
  return { absCol: false, absRow: false };
}

function translateRef(ref: RefNode, dRow: number, dCol: number): Ast {
  const row = ref.absRow ? ref.row : ref.row + dRow;
  const col = ref.absCol ? ref.col : ref.col + dCol;
  if (row < 0 || row >= MAX_ROWS || col < 0 || col >= MAX_COLS) return REF_ERROR;
  return withPosition(ref, row, col);
}

/** Maps one axis coordinate through an insertion. */
function mapInsert(value: number, at: number, count: number, limit: number): AxisResult {
  if (value < at) return { ok: true, value };
  const moved = value + count;
  return moved < limit ? { ok: true, value: moved } : { ok: false, why: 'overflow' };
}

/** Maps one axis coordinate through a deletion. */
function mapDelete(value: number, at: number, count: number): AxisResult {
  if (value < at) return { ok: true, value };
  if (value >= at + count) return { ok: true, value: value - count };
  return { ok: false, why: 'deleted' };
}

function mapAxis(value: number, axis: Axis, inserting: boolean, edit: StructuralEdit): AxisResult {
  return inserting
    ? mapInsert(value, edit.at, edit.count, axisLimit(axis))
    : mapDelete(value, edit.at, edit.count);
}

function rewriteRef(ref: RefNode, axis: Axis, inserting: boolean, edit: StructuralEdit): Ast {
  const mapped = mapAxis(axisOf(ref, axis), axis, inserting, edit);
  return mapped.ok ? withAxis(ref, axis, mapped.value) : REF_ERROR;
}

/**
 * Rewrites a range endpoint-wise. When a deletion removes part of the range,
 * the surviving side is clamped to the edge of the deleted band, which is what
 * keeps `A1:A10` minus rows 3:5 equal to `A1:A7`. When it removes all of it,
 * the range becomes `#REF!`.
 */
function rewriteRange(
  range: RangeNode,
  axis: Axis,
  inserting: boolean,
  edit: StructuralEdit,
): Ast {
  const fromValue = axisOf(range.from, axis);
  const toValue = axisOf(range.to, axis);
  const fromMapped = mapAxis(fromValue, axis, inserting, edit);
  const toMapped = mapAxis(toValue, axis, inserting, edit);
  if (!fromMapped.ok && !toMapped.ok) return REF_ERROR;
  // `from` may be the larger endpoint on a reversed range such as `B10:B2`.
  const fromIsLower = fromValue <= toValue;
  const clampLower = edit.at;
  const clampUpper = edit.at - 1;
  const resolve = (result: AxisResult, isLower: boolean): number | null => {
    if (result.ok) return result.value;
    if (result.why === 'overflow') return null;
    return isLower ? clampLower : clampUpper;
  };
  const fromFinal = resolve(fromMapped, fromIsLower);
  const toFinal = resolve(toMapped, !fromIsLower);
  if (fromFinal === null || toFinal === null) return REF_ERROR;
  return {
    k: 'range',
    from: withAxis(range.from, axis, fromFinal),
    to: withAxis(range.to, axis, toFinal),
  };
}

/**
 * Rebuilds a range from two independently mapped endpoints, degrading to
 * `#REF!` if either endpoint failed. The endpoint mappers preserve the sheet
 * qualifier (or its absence), so the "sheet lives on `from`" invariant holds.
 */
function combineEndpoints(from: Ast, to: Ast): Ast {
  if (from.k !== 'ref' || to.k !== 'ref') return REF_ERROR;
  return { k: 'range', from, to };
}

/** Visits every node once, iteratively, so deep trees cannot overflow the stack. */
function walk(ast: Ast, visit: (node: Ast) => void): void {
  const stack: Ast[] = [ast];
  while (stack.length > 0) {
    const node = stack.pop() as Ast;
    visit(node);
    switch (node.k) {
      case 'call':
        for (let i = node.args.length - 1; i >= 0; i--) stack.push(node.args[i] as Ast);
        break;
      case 'unary':
        stack.push(node.arg);
        break;
      case 'binary':
        stack.push(node.right);
        stack.push(node.left);
        break;
      default:
        break;
    }
  }
}

/**
 * Rebuilds the tree with every reference use replaced by `f(use)`. Nodes with
 * no references inside are returned unchanged, so a rewrite that touches
 * nothing allocates nothing.
 */
function mapRefs(ast: Ast, f: (use: RefUse) => Ast): Ast {
  switch (ast.k) {
    case 'ref':
    case 'range':
      return f(ast);
    case 'call': {
      const args = ast.args.map((arg) => mapRefs(arg, f));
      return args.every((arg, i) => arg === ast.args[i]) ? ast : { ...ast, args };
    }
    case 'unary': {
      const arg = mapRefs(ast.arg, f);
      return arg === ast.arg ? ast : { ...ast, arg };
    }
    case 'binary': {
      const left = mapRefs(ast.left, f);
      const right = mapRefs(ast.right, f);
      return left === ast.left && right === ast.right ? ast : { ...ast, left, right };
    }
    default:
      return ast;
  }
}
